// Local/offline-first session store using localStorage
// Minimal types to avoid extra deps and keep bundle small

export type Rep = { t: number; depth: number; cadence?: number }
export type SetRec = { id: string; reps: number; durationMs: number; repsDetail?: Rep[] }
export type SessionRec = {
  id: string
  startedAt: number
  endedAt?: number
  reps: number
  sets: SetRec[]
  notes?: string
  pausedMs?: number
  version?: number
}

const KEY = 'pt.sessions.v1'

function load(): SessionRec[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') } catch { return [] }
}
function save(sessions: SessionRec[]) {
  try { localStorage.setItem(KEY, JSON.stringify(sessions)) } catch { /* ignore */ }
}

export const store = {
  all(): SessionRec[] { return load().sort((a,b)=>b.startedAt-a.startedAt) },
  add(session: SessionRec) { const all = load(); all.push(session); save(all) },
  update(id: string, patch: Partial<SessionRec>) {
    const all = load(); const i = all.findIndex(s=>s.id===id); if (i>=0) { all[i] = { ...all[i], ...patch }; save(all) }
  },
  remove(id: string) { save(load().filter(s=>s.id!==id)) },
}

// Helpers
export function todayKey(ts = Date.now()) {
  const d = new Date(ts); return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`
}

export function summarizeWeek(endTs = Date.now()) {
  const msDay = 86400000
  const end = new Date(endTs)
  const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).getTime()
  const startTs = endOfDay - (6 * msDay)
  const sessions = store.all().filter(s => s.startedAt >= startTs && s.startedAt <= endOfDay)
  const byDay: Record<string, number> = {}
  for (let i = 0; i < 7; i++) {
    const ts = startTs + i*msDay
    byDay[todayKey(ts)] = 0
  }
  sessions.forEach(s => { byDay[todayKey(s.startedAt)] = (byDay[todayKey(s.startedAt)]||0) + s.reps })
  const total = sessions.reduce((a, s) => a + s.reps, 0)
  const bestSet = Math.max(0, ...sessions.flatMap(s => s.sets.map(set => set.reps)))
  return { byDay, total, bestSet }
}

export function streak(todayTs = Date.now()) {
  const days = new Set(store.all().map(s => todayKey(s.startedAt)))
  let s = 0
  for (let i = 0; ; i++) {
    const ts = todayTs - i * 86400000
    if (days.has(todayKey(ts))) s++
    else break
  }
  return s
}

// Month summary: per-day reps for the current month up to today (inclusive)
export function summarizeMonth(endTs = Date.now()) {
  const d = new Date(endTs)
  const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime()
  const endOfToday = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime()
  const msDay = 86400000
  const byDay: Record<string, number> = {}
  // initialize days 1..today
  for (let ts = start; ts <= endOfToday; ts += msDay) {
    byDay[todayKey(ts)] = 0
  }
  const sessions = store.all().filter(s => s.startedAt >= start && s.startedAt <= endOfToday)
  sessions.forEach(s => { byDay[todayKey(s.startedAt)] = (byDay[todayKey(s.startedAt)] || 0) + s.reps })
  const total = sessions.reduce((a, s) => a + s.reps, 0)
  let bestDay = 0
  for (const k in byDay) bestDay = Math.max(bestDay, byDay[k] || 0)
  return { byDay, total, bestDay }
}
